"use client";

/**
 * Flash Sale 상세 페이지 — 레거시 pages/flash-sale/[slug].js 기반
 *
 * ✅ curl verified 2026-04-18
 * - GET /flash-sale/{slug} → { ...group, returnedStocks: [{ Product, optionInfo }] }
 * - 2-column: 상품 그리드(2/3) + sticky 사이드바(1/3)
 * - 구매 요청 → localStorage(flat + nested 겸용) → /checkout?bundleId=...
 */
import { useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Package } from "lucide-react";

import type { ReturnedStock } from "@/shared/api/services/flashSaleService";
import { BackLink } from "@/shared/ui";

import { useFlashSaleDetail } from "@/features/flash-sale/queries/useFlashSale";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface FlashSaleDetailContentProps {
  slug: string;
}

function StockItem({ stock, discountRate }: { stock: ReturnedStock; discountRate: number }) {
  const originalPrice = stock.price ?? 0;
  const discountedPrice = Math.round(originalPrice * (1 - discountRate / 100));
  const productName = stock.Product?.name ?? "";
  const imagePath = stock.Product?.imagePath;
  const optionName = stock.optionInfo?.name;

  return (
    <Link href={`/product/${stock.product_id}`} className="group block">
      <div className="aspect-square bg-neutral-100 rounded-md overflow-hidden mb-3">
        {imagePath ? (
          <img
            src={imagePath}
            alt={productName}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Package className="h-8 w-8 text-neutral-300" />
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-[13px] leading-[1.4] text-neutral-800 line-clamp-2">{productName}</p>
        {optionName && (
          <p className="text-[11px] text-muted-foreground line-clamp-1">{optionName}</p>
        )}
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold">{discountedPrice.toLocaleString()}원</span>
          {discountRate > 0 && (
            <span className="text-[11px] text-neutral-400 line-through">
              {originalPrice.toLocaleString()}원
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function FlashSaleDetailContent({ slug }: FlashSaleDetailContentProps) {
  const router = useRouter();
  const { data, isLoading, isError } = useFlashSaleDetail(slug);
  const group = data?.data;

  const stocks = group?.returnedStocks ?? [];
  const discountRate = Number(group?.discount_rate ?? 0);
  const totalPrice = stocks.reduce((sum, s) => sum + s.price * s.amount, 0);
  const discountedTotal = Math.floor(totalPrice * (1 - discountRate / 100));

  const handlePurchase = useCallback(() => {
    if (!group) return;
    // Flat fields for CheckoutContent UI + nested Product/optionInfo for backend compat
    const checkoutItems = stocks.map((stock) => ({
      id: stock.id,
      product_id: stock.product_id,
      productName: stock.Product?.name ?? "",
      imagePath: stock.Product?.imagePath ?? "",
      price: stock.price,
      amount: stock.amount,
      idOptionDetail: stock.option_detail_id ?? undefined,
      option_detail_id: stock.option_detail_id,
      optionLabel: stock.optionInfo?.name,
      brandName: group.brandName,
      brandId: group.brand_id,
      discountRate: Number(group.discount_rate),
      discountPrice: Math.round(stock.price * (1 - discountRate / 100)),
      Product: stock.Product,
      optionInfo: stock.optionInfo,
    }));
    localStorage.setItem(
      "checkoutItems",
      JSON.stringify({
        items: checkoutItems,
        bundleId: group.id,
        discountRate: group.discount_rate,
        totalFee: discountedTotal,
      }),
    );
    router.push(
      `/checkout?bundleId=${group.id}&totalFee=${discountedTotal}&discountRate=${group.discount_rate}`,
    );
  }, [router, group, stocks, discountedTotal, discountRate]);

  if (isLoading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <Skeleton className="h-7 w-60 mb-4" />
        <Skeleton className="h-5 w-40 mb-8" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-square w-full rounded-md" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !group) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center gap-6 text-center pt-32 pb-20">
          <div className="relative">
            <div className="absolute -inset-4 bg-neutral-100 rounded-full blur-xl" />
            <Package className="relative h-14 w-14 text-neutral-300" strokeWidth={1} />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">깜짝 할인 정보를 찾을 수 없습니다</p>
            <p className="text-sm text-muted-foreground">이미 종료되었거나 잘못된 주소입니다.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" asChild>
            <Link href="/flash-sale">목록으로 돌아가기</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isEnded = group.status === "ended" || group.status === "sold";
  const isWaiting = group.status === "waiting";

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Back link */}
      <BackLink onClick={() => router.back()} className="mb-6">
        뒤로가기
      </BackLink>

      <div className="flex flex-col-reverse lg:flex-row gap-10">
        {/* Left: product list */}
        <div className="lg:w-2/3">
          {stocks.length > 0 ? (
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
              {stocks.map((stock) => (
                <StockItem key={stock.id} stock={stock} discountRate={discountRate} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-16">
              이 세트에 포함된 상품이 없습니다.
            </p>
          )}
        </div>

        {/* Right: sticky sidebar */}
        <div className="lg:w-1/3">
          <div className="lg:sticky lg:top-8 space-y-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-neutral-500">깜짝 할인</span>
                {discountRate > 0 && (
                  <span className="text-[11px] font-medium tracking-wide text-neutral-900 bg-neutral-100 rounded-full px-2 py-0.5">
                    {Math.floor(discountRate)}% OFF
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-semibold">{group.brandName}</h1>
              {stocks.length > 0 && (
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold tracking-tight">
                    {discountedTotal.toLocaleString()}원
                  </span>
                  {discountRate > 0 && (
                    <span className="text-sm text-neutral-400 line-through">
                      {totalPrice.toLocaleString()}원
                    </span>
                  )}
                </div>
              )}
            </div>

            {isEnded ? (
              <Button disabled className="w-full h-12 rounded-sm text-[14px] font-medium">
                판매 종료
              </Button>
            ) : isWaiting ? (
              <div className="space-y-2">
                <Button
                  className="w-full h-12 rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[14px] font-medium"
                  disabled
                >
                  구매 대기 중
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  다른 고객이 구매를 검토 중입니다.
                </p>
              </div>
            ) : (
              <Button
                className="w-full h-12 rounded-sm bg-neutral-900 text-white text-[14px] font-medium hover:bg-neutral-800"
                onClick={handlePurchase}
              >
                구매하기
              </Button>
            )}

            {stocks.length > 0 && (
              <div className="border-t border-neutral-100 pt-5">
                <h2 className="text-sm font-medium mb-3">번들 구성 상품</h2>
                <ul className="space-y-2">
                  {stocks.map((stock) => (
                    <li key={stock.id} className="text-xs text-neutral-700 flex gap-1.5">
                      <span className="line-clamp-1 flex-1">
                        {stock.Product?.name}
                        {stock.optionInfo?.name && (
                          <span className="text-neutral-400"> | {stock.optionInfo.name}</span>
                        )}
                      </span>
                      <span className="text-neutral-400 shrink-0">× {stock.amount}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
